import React, { useState } from 'react';
import { Copy, Check, Scissors, Eye, Calendar } from 'lucide-react';

/**
 * Compact Editorial CouponBox
 * Premium magazine-style coupon component with reveal functionality
 */
export const CouponBox = ({ coupon, variant = 'default' }) => {
  const [copied, setCopied] = useState(false);
  const [revealed, setRevealed] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(coupon.coupon_code);
    setCopied(true);
    if (!revealed) setRevealed(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleReveal = () => {
    setRevealed(true);
  };

  // Inline variant - ultra compact for within content
  if (variant === 'inline') {
    return (
      <div className="my-4 inline-flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="coupon-box-inline">
        <Scissors className="w-4 h-4 text-amber-600 flex-shrink-0" />
        <span className="text-sm text-gray-700">{coupon.description}</span>
        {revealed ? (
          <button
            onClick={handleCopy}
            className="bg-amber-100 hover:bg-amber-200 text-amber-800 px-3 py-1 rounded font-mono font-bold text-sm smooth-transition flex items-center gap-1"
            data-testid="coupon-code"
          >
            {coupon.coupon_code}
            {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
          </button>
        ) : (
          <button
            onClick={handleReveal}
            className="bg-brand-yellow hover:bg-yellow-400 text-gray-900 px-3 py-1 rounded text-sm font-semibold smooth-transition"
            data-testid="reveal-code-button"
          >
            Reveal
          </button>
        )}
      </div>
    );
  }

  // Default - compact editorial card
  return (
    <div className="my-6 bg-white border-2 border-dashed border-gray-300 rounded-xl overflow-hidden hover:border-brand-yellow smooth-transition group relative" data-testid="coupon-box">
      {/* Scissors decoration */}
      <div className="absolute -top-2 left-6 z-10">
        <div className="bg-white rounded-full p-1.5 shadow-sm border border-gray-200">
          <Scissors className="w-3.5 h-3.5 text-brand-red" />
        </div>
      </div>

      <div className="flex items-stretch">
        {/* Left: Description */}
        <div className="flex-1 p-4 border-r-2 border-dashed border-gray-300">
          <p className="text-gray-800 font-medium text-sm leading-snug mb-2" data-testid="coupon-description">
            {coupon.description}
          </p>
          {coupon.expiry && (
            <div className="flex items-center gap-1 text-xs text-amber-700">
              <Calendar className="w-3 h-3" />
              <span data-testid="coupon-expiry">Until {coupon.expiry}</span>
            </div>
          )}
        </div>

        {/* Right: Code reveal/copy */}
        <div className="flex-shrink-0 w-40 bg-gradient-to-br from-amber-50 to-yellow-50 flex items-center justify-center p-4">
          {!revealed ? (
            <button
              onClick={handleReveal}
              className="w-full bg-brand-yellow hover:bg-yellow-400 text-gray-900 px-4 py-3 rounded-lg font-bold text-sm smooth-transition flex items-center justify-center gap-2"
              data-testid="reveal-code-button"
            >
              <Eye className="w-4 h-4" />
              Reveal Code
            </button>
          ) : (
            <div className="w-full text-center">
              <div className="font-mono font-black text-lg text-brand-red mb-2 tracking-wide select-all" data-testid="coupon-code">
                {coupon.coupon_code}
              </div>
              <button
                onClick={handleCopy}
                className={`w-full px-3 py-2 rounded-lg text-xs font-semibold smooth-transition flex items-center justify-center gap-1 ${
                  copied 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                }`}
                data-testid="copy-coupon-button"
              >
                {copied ? (
                  <>
                    <Check className="w-3 h-3" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="w-3 h-3" />
                    Copy Code
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Side cutouts */}
      <div className="absolute top-1/2 -left-2 w-4 h-4 bg-gray-50 rounded-full -translate-y-1/2"></div>
      <div className="absolute top-1/2 -right-2 w-4 h-4 bg-gray-50 rounded-full -translate-y-1/2"></div>
    </div>
  );
};

export default CouponBox;
